import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ImportAPI } from '../services/api/import.api';
import { ImportAPIFeil } from '../services/api/import.api';
import type { ImportFeilRad, ImportForhandsvisning, ImportKolonne } from '../services/api/types';
import { lesRegneark } from '../components/common/import/lesRegneark';
import { lagCsv } from '../components/common/import/lagCsv';

export type ImportSteg = 'velg' | 'forhandsvis' | 'importerer' | 'ferdig';

const FEILTEKST: Record<number, string> = {
  401: 'Du er logget ut. Logg inn på nytt.',
  403: 'Du har ikke tilgang til å importere.',
  429: 'For mange forsøk. Vent litt og prøv igjen.',
};
const UKJENT = 'Noe gikk galt. Prøv igjen.';

const tilFeil = (err: unknown): ImportFeilRad[] => {
  if (err instanceof ImportAPIFeil) return err.feil;
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return [{ melding: (status !== undefined && FEILTEKST[status]) || UKJENT }];
};

export const useImport = <R, Res>(
  api: ImportAPI<R, Res>,
  kolonner: ImportKolonne[],
  { maksRader }: { maksRader?: number } = {}
) => {
  const [steg, setSteg] = useState<ImportSteg>('velg');
  const [laster, setLaster] = useState(false);
  const [forhandsvisning, setForhandsvisning] = useState<ImportForhandsvisning<R> | null>(null);
  const [feil, setFeil] = useState<ImportFeilRad[]>([]);
  const [resultat, setResultat] = useState<Res | null>(null);
  const [filnavn, setFilnavn] = useState('');
  // Valgte radnr. Bare rader uten feil kan velges; bekreft sender kun disse som ny CSV.
  const [valgte, setValgte] = useState<Set<number>>(new Set());
  // Økes ved tilbakestill/unmount slik at svar fra gamle forespørsler ignoreres.
  const generasjon = useRef(0);
  // Synkron lås: to bekreft-kall i samme batch skal gi ett API-kall.
  const bekrefter = useRef(false);

  // Rader med feil fra forhåndsvisningen kan aldri velges.
  const feilRadnr = useMemo(
    () => new Set((forhandsvisning?.feil ?? []).flatMap((f) => (f.radnr === undefined ? [] : [f.radnr]))),
    [forhandsvisning]
  );
  const velgbare = useMemo(
    () => (forhandsvisning?.rader ?? []).map((r) => r.radnr).filter((nr) => !feilRadnr.has(nr)),
    [forhandsvisning, feilRadnr]
  );

  const veksleRad = useCallback(
    (radnr: number) => {
      if (!velgbare.includes(radnr)) return;
      setValgte((forrige) => {
        const neste = new Set(forrige);
        if (!neste.delete(radnr)) neste.add(radnr);
        return neste;
      });
    },
    [velgbare]
  );

  const veksleAlle = useCallback(() => {
    setValgte((forrige) => (velgbare.length > 0 && forrige.size === velgbare.length ? new Set() : new Set(velgbare)));
  }, [velgbare]);

  useEffect(() => {
    return () => {
      generasjon.current += 1;
    };
  }, []);

  const velgFil = useCallback(
    async (fil: File) => {
      const mi = ++generasjon.current;
      setLaster(true);
      setFeil([]);
      setForhandsvisning(null);
      setFilnavn(fil.name);
      setValgte(new Set());
      try {
        const lest = await lesRegneark(fil, { maksRader });
        if (mi !== generasjon.current) return;
        if ('feil' in lest) {
          setFeil(lest.feil);
          setSteg('velg');
          return;
        }
        const svar = await api.forhandsvis(lest.csv, fil.name);
        if (mi !== generasjon.current) return;
        const svarFeilRadnr = new Set((svar.feil ?? []).flatMap((f) => (f.radnr === undefined ? [] : [f.radnr])));
        setValgte(new Set(svar.rader.map((r) => r.radnr).filter((nr) => !svarFeilRadnr.has(nr))));
        setForhandsvisning(svar);
        setFeil(svar.feil ?? []);
        setSteg('forhandsvis');
      } catch (err) {
        if (mi !== generasjon.current) return;
        setFeil(tilFeil(err));
        setSteg('velg');
      } finally {
        if (mi === generasjon.current) setLaster(false);
      }
    },
    [api, maksRader]
  );

  const bekreft = useCallback(async (): Promise<Res | null> => {
    if (!forhandsvisning || feil.some((f) => f.radnr === undefined) || bekrefter.current) return null;
    const utvalg = forhandsvisning.rader.filter((r) => valgte.has(r.radnr) && !feilRadnr.has(r.radnr));
    if (utvalg.length === 0) return null;
    // Nettleseren sender et utvalg av radene som ny CSV; serveren validerer den som enhver annen fil.
    const csv = lagCsv(
      kolonner.map((k) => k.overskrift),
      utvalg.map((rad) => kolonner.map((k) => String((rad as Record<string, unknown>)[k.felt] ?? '')))
    );
    bekrefter.current = true;
    const mi = ++generasjon.current;
    setSteg('importerer');
    setLaster(true);
    try {
      const svar = await api.bekreft(csv, filnavn);
      if (mi !== generasjon.current) return svar;
      setResultat(svar);
      setSteg('ferdig');
      return svar;
    } catch (err) {
      if (mi !== generasjon.current) return null;
      setFeil(tilFeil(err));
      setSteg('forhandsvis');
      return null;
    } finally {
      bekrefter.current = false;
      if (mi === generasjon.current) setLaster(false);
    }
  }, [api, forhandsvisning, feil, filnavn, valgte, feilRadnr, kolonner]);

  const tilbakestill = useCallback(() => {
    generasjon.current += 1;
    bekrefter.current = false;
    setSteg('velg');
    setLaster(false);
    setForhandsvisning(null);
    setFeil([]);
    setResultat(null);
    setFilnavn('');
    setValgte(new Set());
  }, []);

  return {
    steg,
    laster,
    forhandsvisning,
    feil,
    resultat,
    valgte,
    velgbare,
    feilRadnr,
    velgFil,
    bekreft,
    veksleRad,
    veksleAlle,
    tilbakestill,
  };
};
